import React, { useState, useEffect } from "react";
import { FaRegUser } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useParams } from "react-router-dom";

const EditUser = () => {
  const API_URL = process.env.REACT_APP_FRONTEND_URL;
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState("");
  const [employmentType, setEmploymentType] = useState("");
  const [status, setStatus] = useState("");
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const { employee_no } = useParams();

  // Dummy member object for display
  const member = {
    name: "John Doe",
    memberId: "M12345",
    username: "john.doe",
    email: "john@example.com",
    mobile: "+94770000000",
    role_name: "User",
    status: "Active",
    employmentType: "Admin",
  };

  useEffect(() => {
    if (employee_no) {
      console.log("Editing employee:", employee_no);

      
    }
  }, [employee_no, API_URL]);

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await fetch(`${API_URL}/v1/hris/user/roles`);
        const data = await response.json();

        if (Array.isArray(data)) {
          setRoles(data);
        } else {
          toast.error("Failed to load roles");
        }
      } catch (error) {
        console.error("Error fetching roles:", error);
        toast.error("Error fetching roles");
      }
    };

    fetchRoles();
  }, [API_URL]);

  const updateCredentials = async () => {
    if (!oldPassword || !newPassword || !confirmPassword) {
      toast.warn("All password fields are required.");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("New password and confirm password do not match.");
      return;
    }

    const payload = {
      employee_no, // comes from useParams
      old_password: oldPassword,
      new_password: newPassword,
      confirm_password: confirmPassword,
    };

    try {
      const res = await fetch(`${API_URL}/v1/hris/user/resetPassword`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (res.ok && data?.success) {
        toast.success("Password updated successfully!");
        setOldPassword("");
        setNewPassword("");
        setConfirmPassword("");
      } else {
        toast.error(data?.message || "Failed to update password.");
      }
    } catch (err) {
      console.error("Password update error:", err);
      toast.error("Something went wrong while updating password.");
    }
  };

  return (
    <div className="font-montserrat my-5">
      <h1 className="text-2xl text-gray-400 mb-4">
        Settings <span className="text-gray-700">/ User Management / Edit</span>
      </h1>

      <div className="mt-5">
        <div className="flex items-start gap-6">
          {/* Left Card */}
          <div className="bg-white p-6 rounded-lg shadow-md w-[300px]">
            <div className="flex flex-col items-center">
              <FaRegUser className="w-24 h-24 mb-3 text-blue-500" />
              <p className="text-lg font-semibold text-gray-800">
                {member.name}
              </p>
              <p className="text-sm text-gray-500">
                Member ID: {member.memberId}
              </p>

              <hr className="w-full my-4 border-gray-200" />


            </div>
          </div>

          {/* Right Section */}
          <div className="bg-white p-6 rounded-lg shadow-md w-full">
            {/* Role & Employment */}
            <p className="text-[18px] mb-4 font-semibold">
              Role & Employment Assign
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-5">
              {/* User Role */}
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-1">
                  User Role
                </label>
                <select
                  className="border border-gray-300 rounded-md p-2 text-sm"
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                >
                  <option value="">-- Select Role --</option>
                  {roles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.role_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Employment Type */}
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-1">
                  Employment Type
                </label>
                <select
                  className="border border-gray-300 rounded-md p-2 text-sm"
                  value={employmentType}
                  onChange={(e) => setEmploymentType(e.target.value)}
                >
                  <option value="">-- Select Type --</option>
                  <option value="Admin">Admin</option>
                  <option value="User">User</option>
                </select>
              </div>

              {/* Employee Status */}
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-1">
                  Employee Status
                </label>
                <select
                  className="border border-gray-300 rounded-md p-2 text-sm"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <option value="">-- Select Status --</option>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
            </div>

            <button
              className="bg-blue-500 px-5 py-2 rounded-md text-white hover:bg-blue-700 transition"
              onClick={async (e) => {
                e.preventDefault();

                if (!selectedRole || !employmentType || !status) {
                  toast.warn("Please fill all fields before saving.");
                  return;
                }

                const payload = {
                  employee_no, // from useParams
                  user_role: Number(selectedRole), // role id
                  user_type: employmentType.toLowerCase(), // "admin" or "user"
                  employment: status === "Active" ? "Yes" : "No", // convert status
                };

                try {
                  const res = await fetch(`${API_URL}/v1/hris/user/updateuser-new`, {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(payload),
                  });

                  const data = await res.json();

                  if (res.ok && data?.success) {
                    toast.success("User updated successfully!");
                  } else {
                    toast.error(data?.message || "Failed to update user.");
                  }
                } catch (err) {
                  console.error("Update error:", err);
                  toast.error("Something went wrong while updating user.");
                }
              }}
            >
              Save
            </button>



            {/* Change Credentials */}
            <p className="text-xl font-semibold text-gray-800 mt-8 mb-3">
              Change Username & Password
            </p>

            <div className="bg-orange-100 text-orange-600 p-4 rounded-md mb-6">
              <p className="font-semibold">Ensure that these requirements are met</p>
              <p className="text-sm">
                Minimum 8 characters long, uppercase & symbol
              </p>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                toast.info("Change credentials clicked (UI only).");
              }}
            >
              <div className="grid grid-cols-2 grid-rows-2 gap-4">
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Employee No
                  </label>
                  <input
                    type="text"
                    value={employee_no || ""} // show employee_no from useParams
                    readOnly
                    className="w-2/3 border border-gray-300 rounded-md p-2"
                  />
                </div>


                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Old Password
                  </label>
                  <input
                    type="password"
                    value={oldPassword}
                    onChange={(e) => setOldPassword(e.target.value)}
                    className="w-2/3 border border-gray-300 rounded-md p-2"
                  />
                </div>

                <div className="w-2/3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    New Password
                  </label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full border border-gray-300 rounded-md p-2"
                  />
                </div>

                <div className="w-2/3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full border border-gray-300 rounded-md p-2"
                  />
                </div>
              </div>

              <button
                type="submit"
                className="mt-6 bg-blue-600 text-white px-5 py-2 rounded hover:bg-red-700 transition duration-200"
                onClick={(e) => {
                  e.preventDefault();
                  updateCredentials();
                }}
              >
                Change Credentials
              </button>

            </form>
          </div>
        </div>
      </div>

      {/* Reset confirmation */}
      {showResetConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-[400px]">
            <h2 className="text-lg font-semibold mb-4 text-gray-800">
              Are you sure?
            </h2>
            <p className="mb-6 text-sm text-gray-600">
              This will reset the user’s role and permissions to the default
              state. Do you want to proceed?
            </p>
            <div className="flex justify-end gap-4">
              <button
                className="px-4 py-2 text-sm rounded bg-gray-300 text-gray-700 hover:bg-gray-400"
                onClick={() => setShowResetConfirm(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 text-sm rounded bg-blue-600 text-white hover:bg-blue-700"
                onClick={() => {
                  setShowResetConfirm(false);
                  toast.info("User reset clicked (UI only).");
                }}
              >
                Yes, Reset
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default EditUser;
